import Image from "next/image";

import { useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import config from "@config";
import type { MyLocale } from "@i18n";
import formatRewards from "@utils/formatRewards";
import { type RosettaExtended, useI18n } from "next-rosetta";
import { FiMoreVertical as MoreVert } from "react-icons/fi";

import Deposit from "@components/Modals/Deposit";
import RenameNode from "@components/Modals/RenameNode";
import { TableCell, TableRow } from "@components/Table";
import { useAppSelector } from "@store/hooks";
import { contractMigrationRequired } from "@store/selectors/contract";
import { formatEther } from "viem";

const getDHBonus = (
	amount: number,
	differenceInDays: number,
	i18n: RosettaExtended<MyLocale>,
): { title: string; icon: JSX.Element; apr: number } => {
	const dhTiers = [
		{ icon: "coal", title: "diamondHandsCoalBonus" },
		{ icon: "dh-bronze", title: "diamondHandsBronzeBonus" },
		{ icon: "dh-silver", title: "diamondHandsSilverBonus" },
		{ icon: "dh-gold", title: "diamondHandsGoldBonus" },
	];

	const { bonus, tier } = config.diamondHandTier(amount, differenceInDays);
	const { icon, title } = dhTiers[tier];

	return {
		apr: 1 + bonus,
		icon: (
			<Image
				src={`/assets/icons/${icon}.svg`}
				width={24}
				height={24}
				alt={`Bonus icon ${tier}`}
			/>
		),
		title: i18n.t(`nodes.nodeListRow.${title}`) as string,
	};
};

export default function NodeListRow({ checkRowSelection, node, onRowClick }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const [isRenameOpen, setIsRenameOpen] = useState(false);
	const [isDepositOpen, setIsDepositOpen] = useState(false);
	const i18n = useI18n<MyLocale>();
	const { title } = getDHBonus(
		Number(formatEther(node.amount)),
		node.differenceInDays,
		i18n,
	);
	const isMigrationRequired = useAppSelector(contractMigrationRequired);

	const handleMenu = (event: React.MouseEvent) => {
		setAnchorEl(event.currentTarget);
	};
	const handleMenuClose = () => setAnchorEl(null);
	const handleRenameClick = () => {
		handleMenuClose();
		setIsRenameOpen(true);
	};
	const handleDepositClick = () => {
		handleMenuClose();
		setIsDepositOpen(true);
	};

	return (
		<>
			<TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
				<TableCell padding="checkbox">
					<Checkbox
						color="primary"
						checked={checkRowSelection}
						disabled={node?.rewards === "0"}
						onClick={(event) => onRowClick(event, node.__id)}
						inputProps={{
							"aria-labelledby": `enhanced-table-checkbox-${node.__id}`,
						}}
					/>
				</TableCell>
				<TableCell component="th" scope="row">
					<Tooltip title={title} placement="top">
						<Box display="flex" alignItems="left" width="fit-content">
							<Typography
								sx={{
									display: "inline",
									lineHeight: "24px",
									marginLeft: 1,
									marginTop: node.differenceInDays < 3 ? 0 : 1,
								}}
							>
								{node.name}
							</Typography>
						</Box>
					</Tooltip>
				</TableCell>
				<TableCell align="right" sx={{ color: "#14D3ED", fontWeight: 500 }}>
					{formatRewards(node.amount)}
				</TableCell>
				<TableCell align="right">{node.lastClaim}</TableCell>
				{!isMigrationRequired && (
					<TableCell align="right">{formatRewards(node.rewards)}</TableCell>
				)}
				<TableCell align="right">
					<IconButton size="small" onClick={handleMenu}>
						<MoreVert size={30} />
					</IconButton>
				</TableCell>
			</TableRow>
			<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
				<MenuItem onClick={handleDepositClick}>
					{i18n.t("nodes.nodeListRow.deposit")}
				</MenuItem>
				<MenuItem onClick={handleRenameClick}>
					{i18n.t("nodes.nodeListRow.rename")}
				</MenuItem>
			</Menu>
			{/* Modals */}
			<RenameNode open={isRenameOpen} setOpen={setIsRenameOpen} />
			<Deposit open={isDepositOpen} setOpen={setIsDepositOpen} node={node} />
		</>
	);
}
